import React from 'react';
import PropTypes from 'prop-types';
import { selectSystemProps, Typography, useThemeTokens, getTokensPropType } from '@telus-uds/components-base';
import styled from 'styled-components';
import { htmlAttrs } from '../utils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([htmlAttrs]);
const RibbonWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "Ribbon__RibbonWrapper",
  componentId: "components-web__sc-186270k-0"
})(["width:fit-content;position:", ";z-index:1;left:", ";top:", ";"], _ref => {
  let {
    left,
    top
  } = _ref;
  return left ?? top ? 'absolute' : 'relative';
}, _ref2 => {
  let {
    left
  } = _ref2;
  return left && `${left}px`;
}, _ref3 => {
  let {
    top
  } = _ref3;
  return top && `${top}px`;
});
const RibbonContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "Ribbon__RibbonContainer",
  componentId: "components-web__sc-186270k-1"
})(["display:flex;justify-content:center;background:", ";padding:", ";border-radius:", ";width:fit-content;box-shadow:", ";border-bottom-right-radius:", ";border-bottom-left-radius:", ";"], _ref4 => {
  let {
    backgroundColor
  } = _ref4;
  return backgroundColor;
}, _ref5 => {
  let {
    padding
  } = _ref5;
  return `${padding}`;
}, _ref6 => {
  let {
    borderRadius
  } = _ref6;
  return borderRadius;
}, _ref7 => {
  let {
    boxShadow,
    shouldWrap
  } = _ref7;
  return shouldWrap && boxShadow;
}, _ref8 => {
  let {
    borderRadiusBottomLeft
  } = _ref8;
  return borderRadiusBottomLeft;
}, _ref9 => {
  let {
    borderRadiusBottomRight
  } = _ref9;
  return borderRadiusBottomRight;
});
const RibbonCurve = /*#__PURE__*/styled.div.withConfig({
  displayName: "Ribbon__RibbonCurve",
  componentId: "components-web__sc-186270k-2"
})(["background:", ";width:", ";height:", ";margin-top:-", ";border-radius:0 0 0 100%;position:relative;z-index:-1;overflow:hidden;&::after{content:'';border-bottom-left-radius:", ";position:absolute;height:", ";background:", ";width:", ";}"], _ref10 => {
  let {
    curveBackgroundColor
  } = _ref10;
  return curveBackgroundColor;
}, _ref11 => {
  let {
    curveWidth
  } = _ref11;
  return curveWidth;
}, _ref12 => {
  let {
    curveHeight
  } = _ref12;
  return curveHeight;
}, _ref13 => {
  let {
    curveMarginTop
  } = _ref13;
  return curveMarginTop;
}, _ref14 => {
  let {
    curveAfterRadius
  } = _ref14;
  return curveAfterRadius;
}, _ref15 => {
  let {
    curveAfterHeight
  } = _ref15;
  return curveAfterHeight;
}, _ref16 => {
  let {
    curveAfterBackgroundColor
  } = _ref16;
  return curveAfterBackgroundColor;
}, _ref17 => {
  let {
    curveAfterWidth
  } = _ref17;
  return curveAfterWidth;
});
const Ribbon = /*#__PURE__*/React.forwardRef((_ref18, ref) => {
  let {
    children,
    tokens,
    wrap: shouldWrap = false,
    left,
    top,
    variant = {},
    ...rest
  } = _ref18;
  const {
    backgroundColor,
    borderRadius,
    boxShadowPaddingBottom,
    boxShadowPaddingLeft,
    boxShadowPaddingRight,
    boxShadowPaddingTop,
    boxShadowColor,
    curveAfterBackgroundColor,
    curveAfterHeight,
    curveAfterWidth,
    curveBackgroundColor,
    curveHeight,
    curveAfterRadius,
    curveWidth,
    curveMarginTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    gradient,
    fontColor,
    borderRadiusBottomLeft,
    borderRadiusBottomRight,
    fontName,
    fontWeight,
    fontSize,
    lineHeight
  } = useThemeTokens('Ribbon', tokens, {
    ...variant,
    wrap: shouldWrap
  });
  let background = backgroundColor;
  if (gradient) {
    const {
      type: gradientType,
      angle: gradientAngle,
      stops: gradientColors
    } = gradient;
    const gradientBackground = `${gradientType}-gradient(${gradientAngle}deg, ${gradientColors[0].color}, ${gradientColors[1].color})`;
    background = gradientBackground;
  }
  return /*#__PURE__*/_jsxs(RibbonWrapper, {
    left: left,
    top: top,
    "data-testid": "ribbon-wrapper",
    ref: ref,
    ...selectProps(rest),
    children: [/*#__PURE__*/_jsx(RibbonContainer, {
      shouldWrap: shouldWrap,
      backgroundColor: background,
      padding: `${paddingTop}px ${paddingRight}px ${paddingBottom}px ${paddingLeft}px`,
      borderRadius: `${borderRadius}px`,
      boxShadow: `${boxShadowPaddingTop}px ${boxShadowPaddingRight}px ${boxShadowPaddingBottom}px ${boxShadowPaddingLeft}px ${boxShadowColor}`,
      borderRadiusBottomLeft: `${borderRadiusBottomLeft}px`,
      borderRadiusBottomRight: `${borderRadiusBottomRight}px`,
      children: /*#__PURE__*/_jsx(Typography, {
        variant: {
          bold: true,
          inverse: true
        },
        tokens: {
          color: fontColor,
          fontName,
          fontWeight,
          fontSize,
          lineHeight
        },
        children: children
      })
    }), shouldWrap && /*#__PURE__*/_jsx(RibbonCurve, {
      "data-testid": "ribbon-curve",
      backgroundColor: backgroundColor,
      curveMarginTop: `${curveMarginTop}px`,
      curveWidth: `${curveWidth}px`,
      curveHeight: `${curveHeight}px`,
      curveBackgroundColor: curveBackgroundColor,
      curveAfterRadius: `${curveAfterRadius}px`,
      curveAfterWidth: `${curveAfterWidth}px`,
      curveAfterHeight: `${curveAfterHeight}px`,
      curveAfterBackgroundColor: curveAfterBackgroundColor
    })]
  });
});
Ribbon.displayName = 'Ribbon';
Ribbon.propTypes = {
  ...selectedSystemPropTypes,
  tokens: getTokensPropType('Ribbon'),
  children: PropTypes.node,
  /** show/hide the ribbon fold
   * @deprecated please don't rely on `wrap` to turn off the wrapping (use `Badge` instead) as it will be removed in the future
   */
  wrap: PropTypes.bool,
  /** sets the left offset (triggers absolute positioning) */
  left: PropTypes.number,
  /** sets the top offset (triggers absolute positioning) */
  top: PropTypes.number
};
export default Ribbon;